import { useState } from 'react';
import { faRobot } from '@/fontawesome';
import { APIRoutes } from '@/services/api.routes';
import { formatCurrencyCentValue } from '@/utils/helpers';
import { pnlColorClass, JOURNAL_FIELD_CLASS, JOURNAL_LABEL_CLASS } from '@/components/journal/journalHelpers';
import { showSuccessToast, showErrorToast } from '@/utils/toasts';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import Card from '@/components/ui/Card';
import ErrorState from '@/components/ui/ErrorState';
import type { JournalSummary } from '@/models';

function pad(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

function isoWeek(d: Date): string {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (date.getUTCDay() + 6) % 7;
  date.setUTCDate(date.getUTCDate() - dayNum + 3);
  const firstThursday = new Date(Date.UTC(date.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((date.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return `${date.getUTCFullYear()}-W${pad(week)}`;
}

type PeriodOption = 'THIS_WEEK' | 'LAST_WEEK' | 'THIS_MONTH' | 'LAST_MONTH';

const OPTION_LABELS: Record<PeriodOption, string> = {
  THIS_WEEK: 'Diese Woche',
  LAST_WEEK: 'Letzte Woche',
  THIS_MONTH: 'Dieser Monat',
  LAST_MONTH: 'Letzter Monat',
};

function resolvePeriod(option: PeriodOption): { periodType: string; period: string } {
  const now = new Date();
  if (option === 'THIS_WEEK') return { periodType: 'week', period: isoWeek(now) };
  if (option === 'LAST_WEEK') {
    const d = new Date(now);
    d.setDate(d.getDate() - 7);
    return { periodType: 'week', period: isoWeek(d) };
  }
  if (option === 'THIS_MONTH') return { periodType: 'month', period: `${now.getFullYear()}-${pad(now.getMonth() + 1)}` };
  const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { periodType: 'month', period: `${lm.getFullYear()}-${pad(lm.getMonth() + 1)}` };
}

export default function JournalAiSummaryCard() {
  const [option, setOption] = useState<PeriodOption>('THIS_MONTH');
  const [summary, setSummary] = useState<JournalSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setSummary(null);
    try {
      const { periodType, period } = resolvePeriod(option);
      const result = await APIRoutes.generateJournalSummary(periodType, period);
      setSummary(result);
      showSuccessToast('KI-Rückblick erstellt');
    } catch (err) {
      console.error('Failed to generate AI summary:', err);
      setError('KI-Rückblick konnte nicht erstellt werden.');
      showErrorToast('KI-Rückblick konnte nicht erstellt werden');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card
      title="KI-Rückblick"
      icon={faRobot}
      actions={
        <>
          <label htmlFor="journal-summary-period" className={`${JOURNAL_LABEL_CLASS} sr-only`}>Zeitraum</label>
          <select
            id="journal-summary-period"
            className={`${JOURNAL_FIELD_CLASS} cursor-pointer`}
            value={option}
            onChange={(e) => setOption(e.target.value as PeriodOption)}
          >
            {(Object.keys(OPTION_LABELS) as PeriodOption[]).map((o) => (
              <option key={o} value={o}>{OPTION_LABELS[o]}</option>
            ))}
          </select>
          <Button onClick={handleGenerate} loading={loading} icon={faRobot}>Erstellen</Button>
        </>
      }
    >
      {error !== null && <ErrorState message={error} onRetry={handleGenerate} />}

      {loading && (
        <p className="text-sm text-gray-500 dark:text-gray-400">KI-Rückblick wird erstellt …</p>
      )}

      {summary === null && error === null && !loading && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Lass dir einen humorvollen KI-Rückblick deiner Trading-Periode erstellen.</p>
      )}

      {summary !== null && (
        <div className="space-y-3">
          <p className="text-gray-800 dark:text-gray-100 leading-relaxed whitespace-pre-line">{summary.summary}</p>
          <div className="flex flex-wrap gap-2">
            <Badge color="gray">{summary.metrics.closedTradeCount} Trades</Badge>
            <Badge color="green">{summary.metrics.winCount} Gewinne</Badge>
            <Badge color="red">{summary.metrics.lossCount} Verluste</Badge>
            <Badge colorClass={`bg-gray-100 dark:bg-gray-700 ${pnlColorClass(summary.metrics.realizedPnL)}`}>
              {formatCurrencyCentValue(summary.metrics.realizedPnL, 'USD')}
            </Badge>
          </div>
        </div>
      )}
    </Card>
  );
}
